import { BoxProps } from 'UIKit/Box/types'
import TransText from 'UIKit/Text/TransText'
import { InhouseGameDetails } from 'config/types/game'
import { useImmutableValueReference } from 'hooks/useImmutableValueReference'
import useMatchBreakpoints from 'hooks/useMatchBreakpoints'
import Column from 'layout/Components/Column'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useValidatePlayToken } from 'state/app/hooks'
import { useTokenSelected } from 'state/session/hooks'
import { HunnyToast } from 'utils/toastify'
import { InhouseGameSettings } from 'views/InhouseGame/Mine/config/types'
import BetBoardTabs from 'views/InhouseGame/components/BetBoardTabs'
import BetAmount from 'views/InhouseGame/components/Controls/BetAmount'
import RiskSelect from 'views/InhouseGame/components/Controls/RiskSelect'
import RowSelect from 'views/InhouseGame/components/Controls/RowSelect'
import Totalbet from 'views/InhouseGame/components/Controls/Totalbet'
import { StyledWrapper } from 'views/InhouseGame/components/styled'
import { GameInHouseConfig, InhouseGameBetBoardTab, InhouseGameBetResult } from 'views/InhouseGame/config/types'
import { usePlinkoAction, usePlinkoGameStatus } from '../../hooks'
import { Risk } from '../GameBoard/config'
import PlinkoBetAction from './PlinkoBetAction'

const PlinkoBetSettings: React.FC<
  {
    risk: Risk
    onSelectRisk: (risk: Risk) => void
    row: number
    onSelectRow: (row: number) => void
    game: InhouseGameDetails
    gameSettings: InhouseGameSettings
  } & GameInHouseConfig &
    Omit<BoxProps, 'onVolumeChange'>
> = ({
  risk,
  onSelectRisk,
  row,
  onSelectRow,
  volumeEnable,
  instanceBetEnable,
  maxBetButtonEnable,
  hotkeyEnable,
  gameSettings,
  game,
  ...props
}) => {
  const [tab, setTab] = useState(InhouseGameBetBoardTab.Manual)
  const playToken = useTokenSelected()
  const { t } = useTranslation()

  const { submitBet } = usePlinkoAction()
  const { isPlaying } = usePlinkoGameStatus()
  const isPlayingRef = useImmutableValueReference(isPlaying)
  const { isDesktop } = useMatchBreakpoints()

  const [betTokenAmount, setBetTokenAmount] = useState(null)
  const [isAmountInvalid, setIsAmountInvalid] = useState(false)
  const isAmountInvalidRef = useImmutableValueReference(isAmountInvalid)
  const [totalBet, setTotalBet] = useState<number>(0)

  const [isAutoBetRunning, setIsAutoBetRunning] = useState(false)
  const refAutobetInterval = useRef(null)
  const isAutoBet = tab === InhouseGameBetBoardTab.Auto

  const { isBlackList } = useValidatePlayToken(playToken)

  const disabled = isAmountInvalid || isBlackList || isAutoBetRunning || game.isMaintenance

  const isGameOutOfViewRef = useRef(false)

  useEffect(() => {
    const handler = () => {
      if (document.hidden) {
        isGameOutOfViewRef.current = true
      } else {
        isGameOutOfViewRef.current = false
      }
    }

    document.addEventListener('visibilitychange', handler)

    return () => {
      document.removeEventListener('visibilitychange', handler)
    }
  }, [])

  const handleBet = useCallback(async () => {
    if (disabled) return

    return submitBet(betTokenAmount, risk, row, {
      instanceBetEnable: instanceBetEnable || isGameOutOfViewRef.current,
      volumeEnable,
    })
  }, [submitBet, disabled, betTokenAmount, volumeEnable, instanceBetEnable, risk, row])

  const handleAutobet = useCallback(
    async (runningStatus: boolean, onCompleted?: () => void) => {
      const isInfinity = totalBet == 0 || !totalBet
      const isDisabled = isAutoBet ? !isAutoBetRunning && (isAmountInvalid || isBlackList) : disabled
      if (isDisabled) return

      if (!runningStatus) {
        if (refAutobetInterval.current) {
          clearInterval(refAutobetInterval.current)
          refAutobetInterval.current = null
          setIsAutoBetRunning(false)
        }
        return
      }

      setIsAutoBetRunning(true)

      let clonedTotalBet = totalBet
      let isInsufficientBalance = false

      refAutobetInterval.current = setInterval(async () => {
        if (clonedTotalBet === 0 && !isInfinity) {
          if (onCompleted) onCompleted()
          clearInterval(refAutobetInterval.current)
          setIsAutoBetRunning(false)
          return
        }

        if (isAmountInvalidRef.current && isInsufficientBalance) {
          if (!isPlayingRef.current) {
            HunnyToast.error(t('Insufficient balance'))
            handleAutobet(false, onCompleted)
          }
          return
        }

        const result = await handleBet()
        isInsufficientBalance = result === InhouseGameBetResult.InsufficientBalance

        if (result === InhouseGameBetResult.Succeed) {
          if (!isInfinity) {
            clonedTotalBet--
            setTotalBet(clonedTotalBet)
          }
        } else if (!isPlayingRef.current) {
          if (result === InhouseGameBetResult.InsufficientBalance) {
            HunnyToast.error(t('Insufficient balance'))
          } else {
            HunnyToast.error(t('Something went wrong'))
          }

          handleAutobet(false, onCompleted)
        }
      }, 1000)
    },
    [handleBet, totalBet],
  )

  return (
    <StyledWrapper {...props}>
      <BetBoardTabs tab={tab} setTab={setTab} disabled={isAutoBetRunning || game.isMaintenance} />

      <Column>
        <BetAmount
          inputId="plinko-bet-amount-input"
          setBetTokenAmount={setBetTokenAmount}
          setIsAmountInvalid={setIsAmountInvalid}
          disabled={isAutoBetRunning || game.isMaintenance}
          mb="14px"
          height="40px"
          placeholder="0"
          maxButton={maxBetButtonEnable}
          hotKey={hotkeyEnable}
          isBlackListToken={isBlackList}
          maxBet={gameSettings.maxBetAmountUsd}
        />
        {!isDesktop && (
          <PlinkoBetAction
            isAutoBetRunning={isAutoBetRunning}
            hotkeyEnable={hotkeyEnable}
            mode={isAutoBet ? 'autoBet' : 'singleBet'}
            mb="14px"
            disabled={isAutoBet ? isAmountInvalid || isBlackList : disabled}
            onBet={handleBet}
            onAutoBet={handleAutobet}
          />
        )}

        <RiskSelect
          risk={risk}
          onSelect={onSelectRisk}
          disabled={isPlaying || isAutoBetRunning || game.isMaintenance}
          mb="14px"
          height="40px"
        />
        <RowSelect
          row={row}
          onSelect={onSelectRow}
          disabled={isPlaying || isAutoBetRunning || game.isMaintenance}
          mb="14px"
          height="40px"
          dropdownPosition={isDesktop ? 'bottom' : 'top'}
        />
        {isAutoBet && (
          <Totalbet
            totalBet={totalBet}
            onChangeTotalBet={setTotalBet}
            disabled={isAutoBetRunning || isAutoBetRunning}
            mb="14px"
            height="40px"
          />
        )}
      </Column>

      {isDesktop && (
        <PlinkoBetAction
          isAutoBetRunning={isAutoBetRunning}
          hotkeyEnable={hotkeyEnable}
          mode={isAutoBet ? 'autoBet' : 'singleBet'}
          disabled={isAutoBet ? !isAutoBetRunning && (isAmountInvalid || isBlackList) : disabled}
          onBet={handleBet}
          onAutoBet={handleAutobet}
        />
      )}

      {game.isMaintenance && (
        <TransText small textAlign="center" mt="8px" lineHeight="1.2">
          This game is under maintenance
        </TransText>
      )}
    </StyledWrapper>
  )
}

export default PlinkoBetSettings
